import { useEffect, useRef, useState } from "react";
import { FileText } from "lucide-react";

import { Card } from "../../../shared/card/card.tsx";
import { showError } from "../../../../utils/toast.ts";
import { isSqeLabsEmployeeType } from "../../../../hooks/use-logged-in-employee.ts";
import { createAppraisalLetterHtml, type AppraisalLetterPreviewData } from "../appraisal-letter-preview.ts";
import styles from "../employee-appraisal.module.css";

type Props = {
  data: AppraisalLetterPreviewData;
  className?: string;
  fillHeight?: boolean;
};

export function PreviewAppraisalLetter({ data, className, fillHeight = false }: Props) {
  const isSqeAppraisal = isSqeLabsEmployeeType(data.employeeType);
  const [documentHtml, setDocumentHtml] = useState("");
  const [previewFailed, setPreviewFailed] = useState(false);
  const [sqePageCount, setSqePageCount] = useState<number | null>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    let active = true;
    setDocumentHtml("");
    setPreviewFailed(false);
    void createAppraisalLetterHtml(data)
      .then((html) => { if (active) setDocumentHtml(html); })
      .catch((cause: unknown) => { if (active) { setPreviewFailed(true); showError(cause instanceof Error ? cause.message : "Unable to load letter preview.", { id: "appraisal-letter-preview-error" }); } });
    return () => { active = false; };
  }, [data]);

  useEffect(() => {
    if (!documentHtml || !isSqeAppraisal) return;
    setSqePageCount(null);
    const interval = window.setInterval(() => {
      const frameDocument = iframeRef.current?.contentDocument;
      if (frameDocument?.body?.dataset.paginationReady === "true") {
        setSqePageCount(frameDocument.querySelectorAll(".pdf-page").length);
        window.clearInterval(interval);
      }
    }, 100);
    return () => window.clearInterval(interval);
  }, [documentHtml, isSqeAppraisal]);

  return <Card padding="none" className={`${className ?? ""} ${fillHeight ? styles.fillHeight : ""}`.trim()}>
    <div className={styles.documentViewer}>
      <div className={styles.viewerBar}><FileText size={16} /><span>Appraisal_Letter_{data.employeeName.replace(/\s+/g, "_")}.pdf</span><span>1 / {isSqeAppraisal ? (sqePageCount ?? "…") : 19}</span><strong>100%</strong></div>
      {documentHtml ? <iframe ref={iframeRef} title="Generated appraisal letter preview" srcDoc={documentHtml} /> : <div className={styles.previewLoading}>{previewFailed ? "Preview unavailable" : "Preparing document preview..."}</div>}
    </div>
  </Card>;
}
